import AppLayout from 'components/AppLayout';
import BackdropLoader from 'app/components/BackdropLoader';
import Initializing from 'components/Initializing';
import { useApp } from 'modules/app/appSelector';
import SubscriptionModals from 'app/components/SubscriptionModals';
import React, { FC, ReactNode, Suspense, useEffect } from 'react';
import { Outlet, RouteObject, useLocation } from 'react-router-dom';
import useSubscriptionInfo from '../app/hooks/useSubscriptionInfo';
import useHeapPlanSync from 'app/hooks/useHeapPlanSync';
import { useCookies } from 'react-cookie';
import { USER_IS_TRIAL } from 'app/config/cookies';
import HotjarUtils from 'app/utils/hotjar';
import routes from 'constants/routes';
import { postsRouter } from 'app/modules/posts';
import { inAppNotificationsRouter } from 'app/modules/inAppNotifications';
import PrivateAppProviders from './components/PrivateAppProviders';
import { insightsRouter } from 'app/modules/insights';
import CommonModals from 'components/modals/CommonModals';
import CommandPalette from 'app/components/commandPalette/CommandPalette';
import DataLayer from 'app/utils/dataLayer';
import { dashboardRouter } from 'app/modules/dashboard';
import { insightsLiteRouter } from 'app/modules/insightsLite';
import { brandHubRouter } from 'app/modules/brandHub';
import { onboardingRouter } from 'app/modules/onboarding';
import OnboardingChecklist from 'app/modules/onboardingChecklist/OnboardingChecklist';
import { useOnboardingChecklistConfig } from 'app/modules/onboardingChecklist/hooks/useOnboardingChecklistConfig';
import { reportsRouter } from './modules/reports';
import { settingsRouter } from 'app/modules/settings';
import { verifyEmailRouter } from 'app/modules/settings/modules/me/modules/personal/verifyEmailRouter';
import SocialLoginContextProvider from 'app/modules/socialLogin/context';
import { approvalsRouter } from './modules/approvals';
import { bioRouter } from 'app/modules/bio';
import { embedRouter } from './modules/embed';
import { aiHubSmokeRouter } from 'app/modules/dev/aiHubSmoke';
import { aiHubEndpointRouter } from 'app/modules/dev/aiHubEndpoint';
import { analyticsRouter } from 'app/modules/analytics';
import AnalyticsSyncCompletionWatcher from 'app/modules/analytics/components/AnalyticsSyncCompletionWatcher';
import { G2ReviewContextProvider } from 'app/context/G2ReviewContext';
import ErrorBoundary from 'components/ErrorBoundary';
import { PendingInvitationModal } from 'app/modules/accountSwitcher';
import G2ReviewModal from 'app/components/G2ReviewModal';
import RouteAccessGuard from 'app/components/RouteAccessGuard';
import { getBasePath } from 'utils/url';
import { getCurrentReturnPath } from 'app/modules/auth/utils/returnPath';
type Props = {
  children: ReactNode;
};

const PrivateApp: FC<Props> = ({ children }) => {
  const app = useApp();
  const location = useLocation();
  const { isTrial } = useSubscriptionInfo();
  const { canShow: canShowOnboardingChecklist } =
    useOnboardingChecklistConfig();
  const setCookie = useCookies([USER_IS_TRIAL])[1];

  useHeapPlanSync();

  useEffect(() => {
    if (app.isInitialized && !app.isUnauthorized) {
      // Single-use credentials arrive in the query string (e.g. the email
      // verification link) and must not reach Google Tag Manager.
      const search = new URLSearchParams(location.search);
      search.delete('token');
      const sanitizedSearch = search.toString();

      DataLayer.push({
        location: {
          pathname: location.pathname,
          search: sanitizedSearch ? `?${sanitizedSearch}` : '',
        },
        event: DataLayer.events.APP_PAGE_VIEW,
      });
    }
  }, [
    app.isInitialized,
    app.isUnauthorized,
    location.pathname,
    location.search,
  ]);

  const isInitializing = !app.isInitialized && !app.isUnauthorized;
  const isUnauthorized = app.isUnauthorized;

  useEffect(() => {
    if (isUnauthorized) {
      const returnPath = getCurrentReturnPath(getBasePath());
      const login = new URL(
        `${getBasePath()}${routes.LOGIN}`,
        window.location.origin,
      );
      login.searchParams.set('returnPath', returnPath);
      window.location.href = login.toString();
    }
  }, [isUnauthorized]);

  useEffect(() => {
    if (app.isInitialized && !app.isUnauthorized) {
      setCookie(USER_IS_TRIAL, isTrial);
      if (isTrial) {
        HotjarUtils.trackEvent(HotjarUtils.eventKey.trialLogin);
      }
    }
  }, [app.isInitialized, app.isUnauthorized, isTrial, setCookie]);

  if (isInitializing) {
    return <Initializing />;
  }

  if (isUnauthorized) {
    return null;
  }

  return (
    <G2ReviewContextProvider>
      <SocialLoginContextProvider>
        <AppLayout>
          <Suspense fallback={<BackdropLoader />}>
            <RouteAccessGuard>{children}</RouteAccessGuard>
            <CommandPalette />
            <CommonModals />
            <SubscriptionModals />
            <ErrorBoundary fallback={<></>}>
              <G2ReviewModal />
            </ErrorBoundary>
            <ErrorBoundary fallback={<></>}>
              <PendingInvitationModal />
            </ErrorBoundary>
            {canShowOnboardingChecklist && <OnboardingChecklist />}
          </Suspense>
        </AppLayout>
      </SocialLoginContextProvider>
    </G2ReviewContextProvider>
  );
};

export const privateRouter: RouteObject = {
  element: (
    <PrivateAppProviders>
      <PrivateApp>
        <AnalyticsSyncCompletionWatcher />
        <Outlet />
      </PrivateApp>
    </PrivateAppProviders>
  ),
  children: [
    postsRouter,
    insightsRouter,
    inAppNotificationsRouter,
    dashboardRouter,
    brandHubRouter,
    insightsLiteRouter,
    onboardingRouter,
    reportsRouter,
    approvalsRouter,
    bioRouter,
    settingsRouter,
    verifyEmailRouter,
    embedRouter,
    analyticsRouter,
    aiHubSmokeRouter,
    aiHubEndpointRouter,
  ],
};
